import { type FC } from "react";
import { ToggleButtonGroup, ToggleButton } from "@mui/material";
import { useTranslation } from "react-i18next";

export type CalendarIntervalValue =
  | "MINUTE"
  | "HOUR"
  | "DAY"
  | "WEEK"
  | "MONTH"
  | "YEAR";

interface AnalyticsCalendarIntervalProps {
  value?: CalendarIntervalValue;
  onChange: (value: CalendarIntervalValue) => void;
  withWeek?: boolean;
  withHour?: boolean;
  withMinute?: boolean;
  withYear?: boolean;
}

export const AnalyticsCalendarInterval: FC<AnalyticsCalendarIntervalProps> = ({
  value = "DAY",
  onChange,
  withWeek,
  withHour,
  withMinute,
  withYear = true,
}) => {
  const { t } = useTranslation();

  const variants: { value: CalendarIntervalValue; label: string }[] = [
    withMinute
      ? { value: "MINUTE", label: t("shared.analyticsCalendarInterval.minute") }
      : null,
    withHour
      ? { value: "HOUR", label: t("shared.analyticsCalendarInterval.hour") }
      : null,
    { value: "DAY", label: t("shared.analyticsCalendarInterval.day") },
    withWeek
      ? { value: "WEEK", label: t("shared.analyticsCalendarInterval.week") }
      : null,
    { value: "MONTH", label: t("shared.analyticsCalendarInterval.month") },
    withYear
      ? { value: "YEAR", label: t("shared.analyticsCalendarInterval.year") }
      : null,
  ].filter(
    (variant): variant is { value: CalendarIntervalValue; label: string } =>
      variant !== null
  );

  return (
    <ToggleButtonGroup
      size="small"
      value={value}
      exclusive
      onChange={(_event, newValue: CalendarIntervalValue | null) => {
        if (newValue) onChange(newValue);
      }}
    >
      {variants.map((variant) => (
        <ToggleButton key={variant.value} value={variant.value}>
          {variant.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};
